/* eslint-disable react/state-in-constructor */

// this could be fixed by using TS or installing PropTypes
// and passing in client as something other than object
// this needs to be fixed in getGroovyFuncs within utils

import 'codemirror/addon/lint/lint';
import 'codemirror/lib/codemirror.css';
// eslint-disable-next-line import/extensions
import 'codemirror/mode/groovy/groovy.js';
import 'codemirror/theme/material.css';
import 'codemirror/theme/neat.css';
import { API } from '../api';
import {
  Button,
  Card,
  Col,
  Dropdown,
  Input,
  Menu,
  Modal,
  Popconfirm,
  Row,
  Space,
  Table,
  notification,
} from 'antd';
import { UnControlled as CodeMirror } from 'react-codemirror2';
import { DimaContext } from '../DimaContext';
import { PlayCircleOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { USER_ROLE } from '../consts';
import { checkPermission, getGroovyFuncs, handleError, isAdmin } from '../utils/util';
import { v4 as uuidv4 } from 'uuid';
import { withRouter } from 'react-router-dom';
import ExportGroovyFunc from './groovyFuncs/exportGroovyFunc';
import ManageGroovyFuncs from './groovyFuncs/manageGroovyFuncs';
import React from 'react';

/**
 * Returns value to be used as data-cy for cypress.
 * May want to move this to utils later when eslint fixing there.
 * @param {*} record
 * @returns
 */
function getBtnDataCy(record) {
  return record.value.slice(0, record.value.indexOf('(')).replaceAll(' ', '_');
}

class GroovyFuncs extends React.Component {
  static contextType = DimaContext;

  constructor(props) {
    super(props);

    this.state = {
      activeModal: null,
      loading: true,
      client: props.client,
      groovyCode: '',
      groovyFuncsSearchText: '',
      selected: { value: '' },
      currentPosition: { line: 0, ch: 0 },
      isNew: false,
      permissionsLoaded: false,
      columns: [
        {
          title: 'Function',
          dataIndex: 'value',
          key: 'value',
          ellipsis: true,
          defaultSortOrder: 'ascend',
          sorter: (a, b) => a.value.localeCompare(b.value),
          render: (_, record) => <code>{record.value.replace('static def', '')}</code>,
        },
        {
          title: '',
          key: 'action',
          width: 80,
          render: (_, record) =>
            (process.env.REACT_APP_FEATURE_PERMISSIONS_V2
              ? checkPermission(this.context.userData.permissions, 'editGroovyFunc')
              : isAdmin(USER_ROLE)) && (
              <Space size="middle">
                <Popconfirm title="Are you sure?" onConfirm={() => this.handleDelete(record)}>
                  <Button data-cy={getBtnDataCy(record)}>X</Button>
                </Popconfirm>
              </Space>
            ),
        },
      ],
      groovyFunctions: [],
    };
  }

  componentDidMount() {
    this.getData();
  }

  componentDidUpdate() {
    // TODO rewrite unsavedGroovyChanges code using new routing
    let unsavedChanges = false;

    if (this.state.groovyCode !== this.state.selected.value || this.state.isNew) {
      unsavedChanges = true;
    }

    if (this.props.unsavedGroovyChanges !== unsavedChanges) {
      this.props.handleUnsavedGroovyChanges(unsavedChanges);
    }

    if (this.context.userData.loaded && !this.state.permissionsLoaded) {
      this.setState({
        permissionsLoaded: true,
        columns: this.generateColumns(),
      });
    }
  }

  /**
   * Called when the user clicks the delete button.
   * Sends request to the DELETE /stdlib/ID API endpoint
   * and then updates the list of groovy functions to show.
   * @param {*} record
   */
  handleDelete = record => {
    const data = [...this.state.groovyFunctions];
    this.setState(
      {
        groovyFunctions: data.filter(func => func.id !== record.id),
        groovyCode: '',
        selected: { value: '' },
        isNew: false,
      },
      async () => {
        try {
          const res = await API.delete(`/stdlib/${record.id}`);
          this.getData();
          notification.success({
            message: `Deleted (${res.status})`,
          });
        } catch (err) {
          handleError(err);
        }
      },
    );
  };

  /**
   * Called when user clicks button to execute Groovy code shown in browser.
   * Sends request to /stdlib/execute with the code to execute in the request
   * body. Based on the response, a message is displayed if the groovy is valid
   * or not.
   *
   * The behavior of the function is a bit weird I think (returns aren't consistent)
   */
  handleExecute = async () => {
    if (this.state.groovyCode.trim() === '') {
      return notification.error({ description: 'Please select a groovy function', duration: 2 });
    }

    const body = {
      groovy: this.state.groovyCode,
      client_id: this.state.client.id,
    };
    this.setState({ loading: true });

    try {
      const res = await API.post('/stdlib/execute', body);
      this.setState({ loading: false });
      const resp = window.location.hostname !== 'localhost' ? JSON.parse(res.data) : res.data;
      const groovyMessage = JSON.parse(resp.body).message;

      if (groovyMessage !== 'VALID GROOVY') {
        resp.statusCode = 400;
      }

      this.setState({ groovyMessage });
      return resp.statusCode === 200;
    } catch (err) {
      handleError(err);
    } finally {
      this.setState({ loading: false });
    }

    return false;
  };

  /**
   * Set state of component after loading the groovy functions.
   */
  getData = async () => {
    this.setState({ loading: true });
    const groovyFunctions = await getGroovyFuncs({ client_id: this.state.client.id });

    this.setState({
      loading: false,
      groovyFunctions,
      isNew: false,
    });
  };

  /**
   * Create new groovy function. Called when the user
   * clicks the "New" button. Generates a new function
   * with the function body just returning true. The function
   * name is generated to end with a random number in [0, 1000].
   */
  createNewGroovyFunc = () => {
    const randNum = Math.floor(Math.random() * 1000);
    const functionTemplate = `static def MyCustomFunction${randNum}(String arg1) {
              return true
          }
          `;
    const selected = {
      id: uuidv4(),
      key: uuidv4(),
      value: functionTemplate,
      client_id: this.state.client.id,
      value_type: 'def',
    };

    const groovyFunctions = [...this.state.groovyFunctions, selected];
    this.setState(
      {
        selected,
        groovyCode: functionTemplate,
        groovyFunctions,
        isNew: true,
      },
      async () => {
        await this.updateGroovyCodeOnServer(true);
      },
    );
  };

  /**
   * Update groovy code on browser
   * @param {String} value
   */
  updateGroovyCodeInBrowser = value => {
    this.setState({ groovyCode: value });
  };

  updateGroovyCodeOnServer = async () => {
    const validGroovy = await this.handleExecute();

    if (window.location.hostname !== 'localhost' && !validGroovy) {
      notification.error({
        message: 'ERROR:',
        description: 'Invalid Groovy function - see error message for details.',
        duration: 6,
      });
      return;
    }

    const { groovyCode, isNew, selected } = this.state;
    selected.value = groovyCode;
    delete selected.key;
    let res;

    if (isNew) {
      res = await API.post(`/stdlib/${selected.id}`, selected);
    } else {
      res = await API.patch(`/stdlib/${selected.id}`, selected);
    }

    notification.success({
      message: `Updated (${res.status})`,
    });
    await this.getData();
  };

  /**
   * Callback function for Mappings search / filter.
   */
  searchGroovyFuncs = e => {
    this.setState({ groovyFuncsSearchText: e.target.value });
  };

  /*
  The delete column is rendered conditionally based on user permissions. Once permissions have been fetched, reset the columns.
  */
  generateColumns() {
    const canEditGroovy = process.env.REACT_APP_FEATURE_PERMISSIONS_V2
      ? checkPermission(this.context.userData.permissions, 'editGroovyFunc')
      : isAdmin(USER_ROLE);

    return [
      {
        title: 'Function',
        dataIndex: 'value',
        key: 'value',
        ellipsis: true,
        defaultSortOrder: 'ascend',
        sorter: (a, b) => a.value.localeCompare(b.value),
        render: (_, record) => <code>{record.value.replace('static def', '')}</code>,
      },
      {
        title: '',
        key: 'action',
        width: 80,
        render: (_, record) =>
          canEditGroovy && (
            <Space size="middle">
              <Popconfirm title="Are you sure?" onConfirm={() => this.handleDelete(record)}>
                <Button data-cy={getBtnDataCy(record)}>X</Button>
              </Popconfirm>
            </Space>
          ),
      },
    ];
  }

  render() {
    const { groovyFunctions } = this.state;
    const groovyFuncsVisible = groovyFunctions.filter(item =>
      item.value.toLowerCase().includes(this.state.groovyFuncsSearchText.toLowerCase()),
    );

    const rowSelection = {
      onSelect: record => this.setState({ selected: record }),
      getCheckboxProps: () => ({ disabled: false }),
    };

    const borderStyle = this.props.unsavedGroovyChanges ? 'orange' : 'lightgray';

    const saveMenu = (
      <Menu>
        <Menu.Item key="1" onClick={() => this.setState({ activeModal: 'export' })}>
          Save To Shared Library
        </Menu.Item>
      </Menu>
    );

    const createMenu = (
      <Menu>
        <Menu.Item key="1" onClick={() => this.setState({ activeModal: 'import' })}>
          From Shared Library
        </Menu.Item>
      </Menu>
    );

    const canEditGroovy = process.env.REACT_APP_FEATURE_PERMISSIONS_V2
      ? checkPermission(this.context.userData.permissions, 'editGroovyFunc')
      : isAdmin(USER_ROLE);

    // When mounting the CodeMirror editor, add the CM instance to window variables
    // We do this so that the editor can be referenced in the Cypress test.
    const codeMirror = (
      <CodeMirror
        editorDidMount={editor => {
          window.editor = editor;
        }}
        value={this.state.selected.value}
        options={{
          mode: 'groovy',
          theme: 'neat',
          lineNumbers: true,
          indentUnit: 2,
          lineWrapping: true,
          readOnly: !canEditGroovy,
        }}
        cursor={this.state.currentPosition}
        onChange={(_, __, value) => {
          this.updateGroovyCodeInBrowser(value);
        }}
      />
    );

    return (
      <>
        <Modal
          destroyOnClose
          footer={null}
          onCancel={() => this.setState({ activeModal: null })}
          title="Import Groovy Function"
          visible={this.state.activeModal === 'import'}
          width={1000}
        >
          <ManageGroovyFuncs
            clientId={this.props.client.id}
            getData={this.getData}
            closeModal={() => this.setState({ activeModal: null })}
            groovyFunctions={groovyFunctions}
            mode="import"
          />
        </Modal>
        <Modal
          destroyOnClose
          footer={null}
          onCancel={() => this.setState({ activeModal: null })}
          title="Export Groovy Function"
          visible={this.state.activeModal === 'export'}
          width={1000}
        >
          <ExportGroovyFunc
            closeModal={() => this.setState({ activeModal: null })}
            getData={this.getData}
            groovyCode={this.state.groovyCode}
          />
        </Modal>
        <Row justify="start" align="top" gutter={[40, 16]}>
          <Col span={8}>
            <Input
              data-cy="search-groovy-funcs-input"
              style={{ width: '350px', marginBottom: '14px' }}
              addonBefore="Search:"
              onChange={this.searchGroovyFuncs}
            />
            <Table
              rowSelection={{ type: 'radio', ...rowSelection }}
              columns={this.state.columns}
              loading={this.state.loading}
              dataSource={groovyFuncsVisible}
              size="small"
            />
          </Col>
          <Col span={16}>
            <Row justify="end" align="top" gutter={[40, 16]}>
              {canEditGroovy && (
                <Col>
                  <Dropdown.Button
                    id="create-groovy-dropdown"
                    overlay={createMenu}
                    loading={this.state.loading}
                    type="primary"
                    onClick={() => this.createNewGroovyFunc()}
                  >
                    <Space>
                      <PlusCircleOutlined />
                      Add new function
                    </Space>
                  </Dropdown.Button>
                </Col>
              )}
              <Col flex={1} />
              <Col>
                <Button
                  loading={this.state.loading}
                  onClick={this.handleExecute}
                  style={{ marginRight: '2px' }}
                  type="primary"
                  disabled={this.state.groovyCode.trim() === ''}
                >
                  <PlayCircleOutlined />
                  Execute
                </Button>
              </Col>
              {canEditGroovy && (
                <Col>
                  <Dropdown.Button
                    trigger={['click']}
                    id="save-groovy-dropdown"
                    overlay={saveMenu}
                    loading={this.state.loading}
                    style={{ marginRight: '2px' }}
                    type="primary"
                    disabled={this.state.groovyCode.trim() === ''}
                    onClick={() => this.updateGroovyCodeOnServer(false)}
                  >
                    <Space>
                      <SaveOutlined />
                      Save
                    </Space>
                  </Dropdown.Button>
                </Col>
              )}
            </Row>
            <br />
            <Row gutter={[40, 16]}>
              <Col span={14}>
                <div style={{ border: `2px solid ${borderStyle}`, width: '100%' }}>
                  {codeMirror}
                </div>
              </Col>
              <Col span={10}>
                <Card>{this.state.groovyMessage && <p>{this.state.groovyMessage}</p>}</Card>
              </Col>
            </Row>
          </Col>
        </Row>
      </>
    );
  }
}

export default withRouter(GroovyFuncs);
